/**
 * Handles color swatch interaction on product cards to preview variant images.
 * Mouse:    hover previews, mouseleave restores, a click navigates.
 * Touch:    first tap previews and retargets the card link, second tap navigates.
 * Keyboard: Enter navigates.
 *
 * Branching is per interaction (pointerType), not per device: sniffing touch
 * support left mouse users on touch-screen laptops with no hover preview AND
 * swatches that refused to navigate. The swatch also stays an <a> — it was
 * previously swapped for a <span> to block navigation, which removed it from
 * the tab order and from screen readers.
 */
(function () {
  if (window.__cardSwatchHoverInitialized) return;
  window.__cardSwatchHoverInitialized = true;

  // The input type of the interaction in progress, so a click can tell a mouse
  // (already previewed via hover) from a tap (hasn't previewed yet).
  var lastPointerType = '';
  document.addEventListener(
    'pointerdown',
    function (e) {
      lastPointerType = e.pointerType || '';
    },
    true
  );

  function swapImage(el) {
    var card = el.closest('.card-wrapper');
    if (!card) return;
    var mediaDiv = card.querySelector('.card__media .media');
    if (!mediaDiv) return;
    var img = mediaDiv.querySelector('img:first-child');
    if (!img) return;

    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      img.dataset.originalSrcset = img.srcset || '';
    }

    mediaDiv.classList.remove('media--hover-effect');
    var secondImg = mediaDiv.querySelector('img + img');
    if (secondImg) secondImg.style.opacity = '0';
    img.style.opacity = '1';

    var variantImageUrl = el.dataset.variantImage;
    var srcset = '';
    var widths = [165, 360, 533, 720, 940, 1066];
    widths.forEach(function (w) {
      srcset += variantImageUrl.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w, ';
    });
    srcset += variantImageUrl.replace(/width=\d+/, 'width=1500') + ' 1500w';

    img.srcset = srcset;
    img.src = variantImageUrl;

    // Update card links to point to the variant. The swatch is an <a>, so its
    // own href is the variant URL when no explicit one was stashed.
    var variantUrl = el.dataset.variantUrl || el.getAttribute('href');
    if (variantUrl) {
      card.querySelectorAll('a.full-unstyled-link').forEach(function (a) {
        if (!a.dataset.originalHref) a.dataset.originalHref = a.href;
        a.href = variantUrl;
      });
    }
  }

  function restoreImage(el) {
    var card = el.closest('.card-wrapper');
    if (!card) return;
    var mediaDiv = card.querySelector('.card__media .media');
    if (!mediaDiv) return;
    var img = mediaDiv.querySelector('img:first-child');
    if (!img || !img.dataset.originalSrc) return;

    img.src = img.dataset.originalSrc;
    img.srcset = img.dataset.originalSrcset;

    mediaDiv.classList.add('media--hover-effect');
    img.style.opacity = '';
    var secondImg = mediaDiv.querySelector('img + img');
    if (secondImg) secondImg.style.opacity = '';

    card.querySelectorAll('a.full-unstyled-link[data-original-href]').forEach(function (a) {
      a.href = a.dataset.originalHref;
    });
  }

  function setActiveSwatch(el) {
    var swatchesContainer = el.closest('.card__color-swatches');
    if (!swatchesContainer) return;
    swatchesContainer
      .querySelectorAll('.card__color-swatch, .card__color-swatch-link')
      .forEach(function (s) {
        s.classList.remove('card__color-swatch--tap-active');
      });
    el.classList.add('card__color-swatch--tap-active');
  }

  function initSwatchHover(container) {
    var swatchLinks = container.querySelectorAll('.card__color-swatch-link[data-variant-image]');
    swatchLinks.forEach(function (link) {
      if (link.dataset.swatchHoverBound) return;
      link.dataset.swatchHoverBound = 'true';

      link.addEventListener('mouseenter', function () {
        swapImage(link);
      });

      link.addEventListener('mouseleave', function () {
        // A tapped colour is a choice and stays put; only undo hover previews.
        if (!link.classList.contains('card__color-swatch--tap-active')) {
          restoreImage(link);
        }
      });

      link.addEventListener('click', function (e) {
        // Keyboard activation reports detail 0 — Enter should just navigate.
        if (e.detail === 0) return;
        // A mouse click was preceded by hover, so the colour is already
        // previewed; navigate rather than making the user click twice.
        if (lastPointerType === 'mouse') return;
        // Second tap on the colour already showing: let it through.
        if (link.classList.contains('card__color-swatch--tap-active')) return;

        e.preventDefault();
        swapImage(link);
        setActiveSwatch(link);
      });
    });
  }

  function preloadSwatchImages(container) {
    container.querySelectorAll('[data-variant-image]').forEach(function (el) {
      var url = el.dataset.variantImage;
      if (!url) return;
      var img = new Image();
      img.src = url;
    });
  }

  function init() {
    document.querySelectorAll('.card__color-swatches').forEach(function (el) {
      var wrapper = el.closest('.card-wrapper') || el;
      initSwatchHover(wrapper);
      preloadSwatchImages(wrapper);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1 && (node.querySelector('.card__color-swatches') || node.classList.contains('card__color-swatches'))) {
          initSwatchHover(node.closest('.card-wrapper') || node);
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
